import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { NGROK } from "../../../../APIs";
import { Card, CardContent } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Badge } from "../../../../components/ui/badge";
import { Search, ArrowLeft, Globe, Settings, Lock } from "lucide-react";
import {
    FaGitlab,
    FaGithub,
    FaJira,
    FaGoogleDrive,
    FaSlack,
    FaMicrosoft,
    FaConfluence,
    FaGit,
    FaSalesforce,
    FaDropbox,
    FaBitbucket,
    FaTrello,
    FaAtlassian,
} from "react-icons/fa6";
import { SiOkta, SiGooglecloud, SiMiro, SiNotion, SiZendesk, SiZoom } from "react-icons/si";
import GitLabSettings from "./SaasApps/GitLabSettings";
import { SettingProps } from "../interface";
import GithubSettings from "./SaasApps/GithubSettings";

interface SaaSAppSetting {
    id: string;
    name: string;
    description: string;
    icon: any;
    configured: boolean;
    comingSoon?: boolean;
}

const SaaSApps: SaaSAppSetting[] = [
    {
        id: "gitlab",
        name: "GitLab",
        description: "Connect GitLab to discover identities, tokens, repo permissions, and CI/CD activity signals",
        icon: FaGitlab,
        configured: false,
        comingSoon: false,
    },
    {
        id: "github",
        name: "GitHub",
        description: "Discover org members, teams, repo permissions, and audit activity for identity risk insights",
        icon: FaGithub,
        configured: false,
        comingSoon: true,
    },
    {
        id: "bitbucket",
        name: "Bitbucket",
        description: "Ingest workspace users, groups, repo permissions, and activity metadata",
        icon: FaBitbucket,
        configured: false,
        comingSoon: true,
    },
    {
        id: "jira",
        name: "Jira",
        description: "Ingest users, groups, project roles, admin changes, and issue activity metadata",
        icon: FaJira,
        configured: false,
        comingSoon: true,
    },
    {
        id: "confluence",
        name: "Confluence",
        description: "Discover spaces, permissions, external sharing, and content activity metadata",
        icon: FaConfluence,
        configured: false,
        comingSoon: true,
    },
    {
        id: "gdrive",
        name: "Google Drive",
        description: "Discover file sharing exposure, external access, and risky permission changes (metadata only)",
        icon: FaGoogleDrive,
        configured: false,
        comingSoon: true,
    },
    {
        id: "google-workspace",
        name: "Google Workspace",
        description: "Ingest users, groups, admin events, and org-wide policy metadata",
        icon: SiGooglecloud,
        configured: false,
        comingSoon: true,
    },
    {
        id: "microsoft-entra",
        name: "Microsoft Entra ID",
        description: "Discover identities, apps, roles, sign-in risks, and permission grants",
        icon: FaMicrosoft,
        configured: false,
        comingSoon: true,
    },
    {
        id: "okta",
        name: "Okta",
        description: "Ingest users, groups, apps, assignments, and authentication events metadata",
        icon: SiOkta,
        configured: false,
        comingSoon: true,
    },
    {
        id: "slack",
        name: "Slack",
        description: "Discover workspace users, apps, tokens, and admin/security events metadata",
        icon: FaSlack,
        configured: false,
        comingSoon: true,
    },
    {
        id: "notion",
        name: "Notion",
        description: "Discover workspace members, page sharing exposure, and permission changes metadata",
        icon: SiNotion,
        configured: false,
        comingSoon: true,
    },
    {
        id: "dropbox",
        name: "Dropbox",
        description: "Discover file sharing, external access, and risky permission changes (metadata only)",
        icon: FaDropbox,
        configured: false,
        comingSoon: true,
    },
    {
        id: "zendesk",
        name: "Zendesk",
        description: "Ingest agents, roles, admin settings changes, and ticket activity metadata",
        icon: SiZendesk,
        configured: false,
        comingSoon: true,
    },
    {
        id: "zoom",
        name: "Zoom",
        description: "Discover users, roles, meeting/webinar settings, and admin activity metadata",
        icon: SiZoom,
        configured: false,
        comingSoon: true,
    },
    {
        id: "salesforce",
        name: "Salesforce",
        description: "Discover users, profiles, permission sets, connected apps, and audit events metadata",
        icon: FaSalesforce,
        configured: false,
        comingSoon: true,
    },
    {
        id: "trello",
        name: "Trello",
        description: "Discover members, boards, org visibility, and activity metadata",
        icon: FaTrello,
        configured: false,
        comingSoon: true,
    },
    {
        id: "miro",
        name: "Miro",
        description: "Discover users, teams, board sharing exposure, and activity metadata",
        icon: SiMiro,
        configured: false,
        comingSoon: true,
    },
    {
        id: "git",
        name: "Git (Generic)",
        description: "Connect generic Git providers to discover users, repos, permissions and activity metadata",
        icon: FaGit,
        configured: false,
        comingSoon: true,
    },
    {
        id: "atlassian-admin",
        name: "Atlassian Admin",
        description: "Organization-level directory, SCIM users, policies and audit events metadata",
        icon: FaAtlassian,
        configured: false,
        comingSoon: true,
    },
];

const SAAS_ID_TO_ENUM: Record<string, string> = {
    gitlab: "GITLAB",
    github: "GITHUB",
};

export default function SaaSAppsSettings({ onBack }: SettingProps) {
    const [apps] = useState<SaaSAppSetting[]>(SaaSApps);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeApp, setActiveApp] = useState<string | null>(null);
    const [supportedSaasIds, setSupportedSaasIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        const load = async () => {
            try {
                const tenantsResp = await axios.get(`${NGROK}/api/get-all-domains`);
                const tenants = (tenantsResp.data || []) as Array<{ tenantName?: string }>;
                const tenantName = tenants?.[0]?.tenantName;

                if (!tenantName) {
                    setSupportedSaasIds(new Set());
                    return;
                }

                const supportedResp = await axios.get(`${NGROK}/api/saas/get-supported-saas-apps`, {
                    params: { tenantName },
                });

                const raw = supportedResp.data || [];
                const supportedEnums: string[] = Array.isArray(raw)
                    ? raw.map((v) => String(v))
                    : Object.values(raw).map((v) => String(v));

                const supported = new Set<string>();
                for (const [id, enumVal] of Object.entries(SAAS_ID_TO_ENUM)) {
                    if (supportedEnums.includes(enumVal)) supported.add(id);
                }

                setSupportedSaasIds(supported);
            } catch (e) {
                setSupportedSaasIds(new Set());
            }
        };

        load();
    }, []);

    const filteredApps = useMemo(() => {
        if (!searchTerm) return apps;
        const q = searchTerm.toLowerCase();
        return apps.filter((a) => a.name.toLowerCase().includes(q) || a.description.toLowerCase().includes(q));
    }, [apps, searchTerm]);

    const renderActiveApp = () => {
        if (!activeApp || !supportedSaasIds.has(activeApp)) {
            return null;
        }

        switch (activeApp) {
            case "gitlab":
                return <GitLabSettings onBack={() => setActiveApp(null)} />;
            case "github":
                return <GithubSettings onBack={() => setActiveApp(null)} />;
            default:
                return null;
        }
    };

    if (activeApp) return renderActiveApp();

    return (
        <div className="flex-1 space-y-6 p-6">
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="sm" className="gap-2" onClick={onBack}>
                    <ArrowLeft className="w-4 h-4" />
                    Back to Settings
                </Button>

                <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 rounded-xl flex items-center justify-center border border-blue-200 dark:border-blue-700">
                    <Globe className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>

                <div className="flex-1 flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight" data-testid="text-page-title">
                            SaaS Apps
                        </h1>
                        <p className="text-muted-foreground">Configure supported SaaS integrations</p>
                    </div>

                    <div className="relative w-80">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                        <Input
                            placeholder="Search apps..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10"
                            data-testid="input-search-saas-apps"
                        />
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {filteredApps.map((app) => {
                    const IconComponent = app.icon;

                    const isSupported = supportedSaasIds.has(app.id);
                    const disabled = !isSupported;

                    return (
                        <Card
                            key={app.id}
                            className={`border-border/40 transition-all duration-200 ${disabled ? "opacity-70 cursor-not-allowed" : "hover-elevate cursor-pointer"
                                }`}
                            data-testid={`card-saas-${app.id}`}
                            onClick={() => {
                                if (disabled) return;
                                setActiveApp(app.id);
                            }}
                        >
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="w-14 h-14 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 rounded-xl flex items-center justify-center border border-blue-200 dark:border-blue-700">
                                        <IconComponent className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                                    </div>

                                    {disabled ? (
                                        <Badge variant="secondary" className="gap-1">
                                            <Lock className="w-3.5 h-3.5" />
                                            Coming soon
                                        </Badge>
                                    ) : (
                                        <Button
                                            size="sm"
                                            className="gap-2"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setActiveApp(app.id);
                                            }}
                                            data-testid={`button-configure-${app.id}`}
                                        >
                                            <Settings className="w-4 h-4" />
                                            Configure
                                        </Button>
                                    )}
                                </div>

                                <div className="space-y-3">
                                    <h3 className="font-semibold text-lg leading-tight" data-testid={`title-saas-${app.id}`}>
                                        {app.name}
                                    </h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed">{app.description}</p>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {filteredApps.length === 0 && (
                <div className="text-center py-12">
                    <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No apps found</h3>
                    <p className="text-muted-foreground">Try adjusting your search terms or browse all supported apps.</p>
                </div>
            )}
        </div>
    );
}